// src/components/common/Notifications/CompanyDetailsModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Building2,
  MapPin,
  Briefcase,
  GraduationCap,
  Calendar,
  Users,
  Phone,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
} from "lucide-react";
import {
  getStudentCompanyFullDetails,
  acknowledgeMapping,
} from "../../services/notificationServices";
import "../../styles/CompanyDetailsModal.css";

const CompanyDetailsModal = ({ mappingId, onClose, onAcknowledged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Acknowledge states
  const [ackLoading, setAckLoading] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [ackSuccess, setAckSuccess] = useState(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [declarationError, setDeclarationError] = useState(false);

  useEffect(() => {
    fetchDetails();
    // Prevent body scroll
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await getStudentCompanyFullDetails(mappingId);
      setData(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!declarationAccepted) {
      setDeclarationError(true);
      setAckError("Please confirm the declaration before accepting.");
      return;
    }

    setDeclarationError(false);

    try {
      setAckLoading(true);
      setAckError(null);
      await acknowledgeMapping(mappingId, "Accepted");
      setAckSuccess("You have successfully accepted this opportunity!");
      onAcknowledged && onAcknowledged();
    } catch (err) {
      setAckError(err.message);
    } finally {
      setAckLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setAckError("Please provide a reason for rejection.");
      return;
    }
    try {
      setAckLoading(true);
      setAckError(null);
      await acknowledgeMapping(mappingId, "Rejected", rejectReason.trim());
      setAckSuccess("You have rejected this opportunity.");
      onAcknowledged && onAcknowledged();
    } catch (err) {
      setAckError(err.message);
    } finally {
      setAckLoading(false);
    }
  };
 const formatDate = (d) => {
  if (!d) return "—";

  return d.split(",").map(dateStr => {
    const parts = dateStr.trim().split("-");

    let date;

    if (parts[0].length === 4) {
      // YYYY-MM-DD
      const [year, month, day] = parts.map(Number);
      date = new Date(year, month - 1, day);
    } else {
      // DD-MM-YYYY
      const [day, month, year] = parts.map(Number);
      date = new Date(year, month - 1, day);
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }).join(", ");
};

  const alreadyActioned =
    data?.mappingDetails?.acknowledgementStatus === "Rejected" ||
    data?.mappingDetails?.acknowledgementStatus === "Interview Accepted" ||
    (
      data?.mappingDetails?.acknowledgementStatus === "Accepted" &&
      data?.mappingDetails?.currentStatus !== "Interview Scheduled"
    );
  const isInterviewScheduled =
    data?.mappingDetails?.currentStatus === "Interview Scheduled";

  return (
    <div className="cdm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cdm-modal">
        {/* ── HEADER ── */}
        <div className="cdm-modal-header">
          <div className="cdm-header-left">
            <div className="cdm-header-icon">
              <Building2 size={20} />
            </div>
            <div>
              <p className="cdm-header-label">Company Details</p>
              <h2 className="cdm-header-company">
                {data?.placementDetails?.companyName || "Loading…"}
              </h2>
            </div>
          </div>
          <button className="cdm-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* ── BODY ── */}
        <div className="cdm-modal-body">
          {loading && (
            <div className="cdm-state-center">
              <Loader2 size={36} className="cdm-spin" />
              <p>Fetching details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="cdm-state-center cdm-error">
              <AlertCircle size={36} />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* JOB OVERVIEW */}
              <Section title="Job Overview" icon={<Briefcase size={15} />}>
                <Row label="Job Req ID" value={data.placementDetails.jobReqId} />
                <Row label="Job Title" value={data.placementDetails.jobTitle} />
                <Row label="Department" value={data.placementDetails.department} />
                <Row label="Job Location" value={data.placementDetails.jobLocation} />
                <Row
                  label="Salary"
                  value={
                    data.placementDetails.salary
                      ? `₹ ${Number(data.placementDetails.salary).toLocaleString("en-IN")} / year`
                      : "—"
                  }
                />
                <Row label="Openings" value={data.placementDetails.numberOfOpenings ?? "—"} />
                <Row label="Status" value={data.placementDetails.jobReqStatus} />
              </Section>

              {/* JOB DESCRIPTION */}
              {data.placementDetails.jobDescription && (
                <Section title="Job Description" icon={<ClipboardList size={15} />}>
                  <p className="cdm-description">{data.placementDetails.jobDescription}</p>
                </Section>
              )}

              {/* SKILLS */}
              <Section title="Skills Required" icon={<ChevronRight size={15} />}>
                <div className="cdm-skill-chips">
                  {[
                    data.placementDetails.skillSet,
                    ...(data.placementDetails.additionalSkills
                      ? data.placementDetails.additionalSkills.split(",").map((s) => s.trim())
                      : []),
                  ]
                    .filter(Boolean)
                    .map((skill, i) => (
                      <span key={i} className="cdm-skill-chip">
                        {skill}
                      </span>
                    ))}
                </div>
              </Section>

              {/* ELIGIBILITY */}
              <Section title="Eligibility Criteria" icon={<GraduationCap size={15} />}>
                <Row label="Qualification" value={data.placementDetails.qualification} />
                <Row label="Passed Out Year" value={data.placementDetails.passedOutYear} />
                <Row label="Experience" value={data.placementDetails.experienceRequired} />
                <Row label="Allowed Branches" value={data.placementDetails.allowedBranches} />
              </Section>

              {/* INTERVIEW DETAILS */}
              <Section title="Interview Details" icon={<Calendar size={15} />}>
                <Row label="Interview Type" value={data.placementDetails.interviewType} />
                <Row label="Mode" value={data.placementDetails.modeOfInterview} />
                <Row label="Interview Date" value={formatDate(data.placementDetails.interviewDate)} />
                {data.placementDetails.interviewTime && (
                  <Row label="Interview Time" value={data.placementDetails.interviewTime} />
                )}
                {data.placementDetails.interviewLocation && (
                  <Row label="Interview Location" value={data.placementDetails.interviewLocation} />
                )}
                {data.placementDetails.venue && (
                  <Row label="Venue" value={data.placementDetails.venue} />
                )}
                <Row label="No. of Rounds" value={data.placementDetails.numberOfRounds ?? "—"} />
                <Row label="Bond" value={data.placementDetails.bond} />
              </Section>

              {/* ADDITIONAL DETAILS */}
              {data.placementDetails.additionalDetails && (
                <Section title="Additional Details" icon={<AlertCircle size={15} />}>
                  <p className="cdm-description">{data.placementDetails.additionalDetails}</p>
                </Section>
              )}

              {/* CONTACT */}
              {data.placementDetails.createdBy && (
                <Section title="Contact" icon={<Phone size={15} />}>
                  <Row label="Created By" value={data.placementDetails.createdBy} />
                  {data.placementDetails.createdContact && (
                    <Row label="Contact" value={data.placementDetails.createdContact} />
                  )}
                </Section>
              )}

              {/* MAPPING STATUS */}
              <Section title="Your Mapping Status" icon={<Users size={15} />}>
                <Row label="Current Status" value={data.mappingDetails.currentStatus} />
                <Row
                  label="Acknowledgement Status"
                  value={data.mappingDetails.acknowledgementStatus}
                />
                <Row label="Mapped On" value={formatDate(data.mappingDetails.mappedDate)} />
                {data.mappingDetails.acknowledgementDate && (
                  <Row
                    label="Responded On"
                    value={formatDate(data.mappingDetails.acknowledgementDate)}
                  />
                )}
                {data.mappingDetails.acknowledgementFeedback && (
                  <Row label="Your Feedback" value={data.mappingDetails.acknowledgementFeedback} />
                )}
              </Section>

              {/* ── ACTION BUTTONS ── */}
              <div className="cdm-action-zone">
                {ackSuccess ? (
                  <div className="cdm-ack-success">
                    <CheckCircle2 size={20} />
                    <span>{ackSuccess}</span>
                  </div>
                ) : alreadyActioned ? (
                  <div className="cdm-already-actioned">
                    <AlertCircle size={16} />
                    You have already {data.mappingDetails.acknowledgementStatus.toLowerCase()} this
                    opportunity.
                  </div>
                ) : (
                  <>
                    <p className="cdm-action-prompt">
                      Please review the details above and respond:
                    </p>
                    <div className={`cdm-declaration ${declarationError ? "cdm-declaration-error" : ""}`}>
                      <label className={`cdm-declaration-label ${declarationError ? "cdm-declaration-label-error" : ""}`}>
                        <input
                          type="checkbox"
                          checked={declarationAccepted}
                          onChange={(e) => {
                            setDeclarationAccepted(e.target.checked);
                            if (e.target.checked) setDeclarationError(false);
                          }}
                        />
                        <span>
                          {isInterviewScheduled ? (
                            <>
                              I confirm that I am willing to attend the interview{" "}
                              {data?.mappingDetails?.feedback && (
                                <strong>({data.mappingDetails.feedback})</strong>
                              )}
                            </>
                          ) : (
                            <>
                              I confirm that I have reviewed the job description and that my
                              qualifications align with the role. I consent to attend the interview.
                            </>
                          )}
                        </span>
                      </label>
                    </div>
                    {ackError && (
                      <div className="cdm-ack-error">
                        <AlertCircle size={14} />
                        {ackError}
                      </div>
                    )}

                    {/* Reject reason input */}
                    {showRejectInput && (
                      <div className="cdm-reject-box">
                        <label className="cdm-reject-label">Reason for Rejection *</label>
                        <textarea
                          className="cdm-reject-textarea"
                          rows={3}
                          placeholder="Please provide your reason…"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                        />
                        <div className="cdm-reject-actions">
                          <button
                            className="cdm-btn cdm-btn-cancel"
                            onClick={() => {
                              setShowRejectInput(false);
                              setRejectReason("");
                              setAckError(null);
                            }}
                            disabled={ackLoading}
                          >
                            Cancel
                          </button>
                          <button
                            className="cdm-btn cdm-btn-reject-confirm"
                            onClick={handleRejectSubmit}
                            disabled={ackLoading}
                          >
                            {ackLoading ? (
                              <Loader2 size={15} className="cdm-spin" />
                            ) : (
                              <>
                                <XCircle size={15} /> Confirm Reject
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {!showRejectInput && (
                      <div className="cdm-main-actions">
                        <button
                          className="cdm-btn cdm-btn-reject"
                          onClick={() => {
                            setShowRejectInput(true);
                            setAckError(null);
                          }}
                          disabled={ackLoading}
                        >
                          <XCircle size={16} />
                          Reject
                        </button>
                        <button
                          className="cdm-btn cdm-btn-accept"
                          onClick={handleAccept}
                          disabled={ackLoading}                        >
                          {ackLoading ? (
                            <Loader2 size={16} className="cdm-spin" />
                          ) : (
                            <>
                              <CheckCircle2 size={16} />
                              Accept
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Small helpers ── */
const Section = ({ title, icon, children }) => (
  <div className="cdm-section">
    <div className="cdm-section-header">
      {icon}
      <span>{title}</span>
    </div>
    <div className="cdm-section-body">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="cdm-row">
    <span className="cdm-row-label">{label}</span>
    <span className="cdm-row-value">{value || "—"}</span>
  </div>
);

export default CompanyDetailsModal;